import React from 'react';
import {
  Box,
  Checkbox,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Badge,
  TagsInput,
  Code,
  Tooltip,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { fromMantineDateTime } from '../helpers/mantineDates';
import { IconFunction, IconInfoCircle } from '@tabler/icons-react';
import { BaseField, FieldOverride } from '../types/geneva-schema';
import { GenevaApiSelect } from './GenevaApiSelect';
import { GenevaApiItem } from '../hooks/useGenevaLookup';

export interface FieldWithOverride extends BaseField {
  override?: FieldOverride;
  isOverridden?: boolean;
}

interface GenevaFieldRendererProps {
  field: FieldWithOverride;
  value: any;
  onChange: (fieldName: string, value: any) => void;
  onLookupValueChange?: (fieldName: string, item: GenevaApiItem | null) => void;
  readOnly?: boolean;
  required?: boolean;
  showLabel?: boolean;
  showDescription?: boolean;
  showOverrideBadges?: boolean;
  placeholder?: string;
}

/**
 * Reusable field renderer that handles all Geneva field types
 * Extracted from GenevaRecordForm to be used across components
 */
export const GenevaFieldRenderer: React.FC<GenevaFieldRendererProps> = ({
  field,
  value,
  onChange,
  onLookupValueChange,
  readOnly = false,
  required: forcedRequired,
  showLabel = true,
  showDescription = true,
  showOverrideBadges = true,
  placeholder,
}) => {
  const required = forcedRequired !== undefined ? forcedRequired : (field.override?.required ?? field.required);
  const effectiveReadOnly = readOnly || field.override?.readOnly || false;
  const hasOptions = field.override?.options && field.override.options.length > 0;
  const hasComputed = !!field.override?.computed;

  const description = field.override?.help || field.description;

  // Convert camelCase to spaced text
  const formatFieldName = (name: string): string => {
    return name.replace(/([A-Z])/g, ' $1').trim();
  };

  const label = showLabel ? (
    <Group gap="xs">
      <Text size="sm" fw={required ? 600 : 400}>
        {field.override?.friendlyName || formatFieldName(field.xmlTag)}{required && <span style={{ color: 'red' }}> *</span>}
      </Text>
      {showOverrideBadges && field.isOverridden && (
        <Badge size="xs" variant="dot" color="blue">Modified</Badge>
      )}
      {showOverrideBadges && hasComputed && (
        <IconFunction size={12} color="var(--mantine-color-violet-6)" />
      )}
      {showDescription && description && (
        <Tooltip label={description} multiline w={300}>
          <IconInfoCircle size={12} style={{ cursor: 'help' }} />
        </Tooltip>
      )}
    </Group>
  ) : undefined;

  // If field has computed expression, show it as read-only
  if (hasComputed) {
    return (
      <Box key={field.xmlTag}>
        {label}
        <Stack gap="xs">
          <TextInput
            value={String(value)}
            readOnly
            description={showDescription && hasComputed ? (
              <Code style={{ fontSize: '0.75rem' }}>= {field.override!.computed!.expr}</Code>
            ) : undefined}
          />
        </Stack>
      </Box>
    );
  }

  // If field has fixed options, use Select
  if (hasOptions) {
    return (
      <Select
        key={field.xmlTag}
        label={label}
        value={value}
        onChange={(newValue) => onChange(field.xmlTag, newValue)}
        data={field.override!.options!}
        readOnly={effectiveReadOnly}
        // required={required}
        clearable={!required}
        placeholder={placeholder}
        comboboxProps={{
          width: 'auto',
          position: 'bottom-start'
        }}
      />
    );
  }

  // If field has links, use Geneva API lookup Select
  let linkTable = field.links && field.links.length > 0 ? field.links[0] : null;

  // Maybe it has references?
  if (!linkTable) {
    linkTable = field.references != null ? field.references : null;
  }

  if (linkTable) {
    return (
      <GenevaApiSelect
        key={field.xmlTag}
        linkTable={linkTable}
        label={label}
        value={value}
        onChange={(newValue) => onChange(field.xmlTag, newValue)}
        onSelectItem={(item) => onLookupValueChange?.(field.xmlTag, item)}
        disabled={effectiveReadOnly}
        // required={required}
        placeholder={placeholder || `Select ${field.override?.friendlyName || field.xmlTag}`}
        showApiLabel={true}
      />
    );
  }

  // Render based on field type
  switch (field.type.toLowerCase()) {
    case 'boolean':
    case 'binary':
      return (
        <Checkbox
          key={field.xmlTag}
          label={label}
          checked={Boolean(value)}
          onChange={(e) => onChange(field.xmlTag, e.target.checked)}
          readOnly={effectiveReadOnly}
        />
      );

    case 'integer':
    case 'number':
    case 'real':
      return (
        <NumberInput
          key={field.xmlTag}
          label={label}
          value={value}
          onChange={(newValue) => onChange(field.xmlTag, newValue)}
          readOnly={effectiveReadOnly}
          // required={required}
          placeholder={placeholder}
        />
      );

    case 'date':
      return (
        <DateTimePicker
          key={field.xmlTag}
          label={label}
          valueFormat="DD/MM/YYYY HH:mm"
          value={value ? new Date(value) : null}
          onChange={(newValue) => {
            if (newValue) {
              // Default to end of day if previously empty
              const dateToStore = fromMantineDateTime(newValue)!;
              if (!value) {
                dateToStore.setHours(23, 59, 59, 0);
              }
              // Store as local date string to avoid timezone conversion
              const year = dateToStore.getFullYear();
              const month = String(dateToStore.getMonth() + 1).padStart(2, '0');
              const day = String(dateToStore.getDate()).padStart(2, '0');
              const hours = String(dateToStore.getHours()).padStart(2, '0');
              const minutes = String(dateToStore.getMinutes()).padStart(2, '0');
              const seconds = String(dateToStore.getSeconds()).padStart(2, '0');
              const localDateString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
              onChange(field.xmlTag, localDateString);
            } else {
              onChange(field.xmlTag, null);
            }
          }}
          readOnly={effectiveReadOnly}
          // required={required}
          placeholder={placeholder}
        />
      );

    case 'enumerated value':
      // For enums without explicit options, create a text input with suggestion
      return (
        <TextInput
          key={field.xmlTag}
          label={label}
          value={value}
          onChange={(e) => onChange(field.xmlTag, e.target.value)}
          readOnly={effectiveReadOnly}
          // required={required}
          placeholder={placeholder || "Enter enumerated value"}
        />
      );

    default:
      // Check if type starts with 'Date' (handles complex date types)
      if (field.type.toLowerCase().startsWith('date')) {
        return (
          <DateTimePicker
            key={field.xmlTag}
            label={label}
            valueFormat="DD/MM/YYYY HH:mm"
            value={value ? new Date(value) : null}
            onChange={(newValue) => {
              if (newValue) {
                // Default to end of day if previously empty
                const dateToStore = fromMantineDateTime(newValue)!;
                if (!value) {
                  dateToStore.setHours(23, 59, 59, 0);
                }
                // Store as local date string to avoid timezone conversion
                const year = dateToStore.getFullYear();
                const month = String(dateToStore.getMonth() + 1).padStart(2, '0');
                const day = String(dateToStore.getDate()).padStart(2, '0');
                const hours = String(dateToStore.getHours()).padStart(2, '0');
                const minutes = String(dateToStore.getMinutes()).padStart(2, '0');
                const seconds = String(dateToStore.getSeconds()).padStart(2, '0');
                const localDateString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
                onChange(field.xmlTag, localDateString);
              } else {
                onChange(field.xmlTag, null);
              }
            }}
            readOnly={effectiveReadOnly}
            // required={required}
            placeholder={placeholder}
          />
        );
      }

      // Default to text input for strings and other types
      if (field.multiple) {
        return (
          <TagsInput
            key={field.xmlTag}
            label={label}
            value={Array.isArray(value) ? value : value ? [value] : []}
            onChange={(newValue) => onChange(field.xmlTag, newValue)}
            readOnly={effectiveReadOnly}
            // required={required}
            placeholder={placeholder}
          />
        );
      }


      return (
        <TextInput
          key={field.xmlTag}
          label={label}
          value={value}
          onChange={(e) => onChange(field.xmlTag, e.target.value)}
          readOnly={effectiveReadOnly}
          // required={required}
          placeholder={placeholder}
        />
      );
  }
};
